import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelSx } from '../common/formFieldStyles';
import MoloniService from '../../services/moloniService';
import { BRAND } from '../../theme/brandTokens';
import { describeApiError } from '../../utils/stockErrorUtils';
import { describeInvoiceError } from '../../utils/moloniError';
import { getAppointmentStatusKey, getPaymentStatusKey } from '../../utils/appointmentStatusUtils';

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '2px solid var(--brand-line, #E3DDD4)',
  borderRadius: 8,
  minHeight: 48,
  padding: '0 12px',
  font: 'inherit',
};

const IssueInvoiceDialog = ({
  open,
  storeId,
  sourceKey,
  onClose,
  onIssued,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !storeId || !sourceKey) {
      return undefined;
    }

    let cancelled = false;
    setError('');
    MoloniService.preview(storeId, sourceKey)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setForm({
          name: data.customerName || data.CustomerName || '',
          vat: data.customerVat || data.CustomerVat || '',
          email: data.email || data.Email || '',
          address: '',
          city: '',
          postalCode: '',
        });
      })
      .catch((requestError) => {
        if (!cancelled) setError(describeInvoiceError(requestError, t));
      });

    return () => {
      cancelled = true;
    };
  }, [open, storeId, sourceKey, t]);

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));

  const issue = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await MoloniService.issue(storeId, sourceKey, form);
      onIssued?.(result);
      onClose();
    } catch (requestError) {
      setError(describeInvoiceError(requestError, t));
    } finally {
      setSaving(false);
    }
  };

  const missing = preview?.missingExemption || preview?.MissingExemption;

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('moloni.confirmTitle')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={saving}>{t('moloni.later')}</Button>
          <PrimaryActionButton onClick={issue} disabled={saving || Boolean(missing)}>{t('moloni.issue')}</PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {missing ? <Typography color="error">{t('moloni.missingExemption', { name: missing })}</Typography> : null}
        {error ? <Typography color="error">{error}</Typography> : null}
        {[
          ['name', 'moloni.name'],
          ['vat', 'vat.fields.number'],
          ['email', 'moloni.email', 'moloni.emailOnIssue'],
          ['address', 'moloni.address'],
          ['city', 'moloni.city'],
          ['postalCode', 'moloni.postalCode'],
        ].map(([name, label, hint]) => (
          <Box key={name}>
            <Typography variant="subtitle2" sx={fieldLabelSx}>{t(label)}</Typography>
            <input value={form[name] || ''} onChange={setField(name)} style={fieldStyle} />
            {hint ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: BRAND.textMuted }}>{t(hint)}</Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </ScrollableDialog>
  );
};

export const MoloniConnectionPanel = ({ storeId, canWrite, onReadyChange }) => {
  const { t } = useTranslation();
  const [connection, setConnection] = useState(null);
  const [companyId, setCompanyId] = useState('');
  const [documentSetId, setDocumentSetId] = useState('');
  const [sets, setSets] = useState([]);
  const [editing, setEditing] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    MoloniService.getConnection(storeId)
      .then((data) => {
        const companies = data.companies || data.Companies || [];
        const savedCompany = data.companyId ?? data.CompanyId ?? '';
        setConnection(data);
        setCompanyId(savedCompany === '' && companies.length === 1
          ? (companies[0].companyId || companies[0].CompanyId)
          : savedCompany);
        setDocumentSetId(data.documentSetId || data.DocumentSetId || '');
        setSets(data.documentSets || data.DocumentSets || []);
      })
      .catch((requestError) => setError(describeApiError(requestError, t)));
  }, [storeId, t]);

  useEffect(() => {
    if (storeId) load();
  }, [storeId, load]);

  const isReady = Boolean(connection?.ready ?? connection?.Ready);
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);

  useEffect(() => {
    const ready = connection?.ready ?? connection?.Ready;
    if (!storeId || (ready && !editing) || companyId === '' || companyId == null) {
      return undefined;
    }

    let cancelled = false;
    MoloniService.getDocumentSets(storeId, companyId)
      .then((list) => {
        if (cancelled) return;
        const next = list || [];
        setSets(next);
        setDocumentSetId((current) => {
          const ids = next.map((set) => String(set.documentSetId || set.DocumentSetId));
          if (current !== '' && current != null && ids.includes(String(current))) return current;
          return next.length === 1 ? (next[0].documentSetId || next[0].DocumentSetId) : '';
        });
        if (next.length === 0) setError(t('moloni.noSeries'));
      })
      .catch((requestError) => {
        if (!cancelled) setError(describeApiError(requestError, t));
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, connection, editing, storeId, t]);

  const connect = async () => {
    const url = await MoloniService.connect(storeId);
    window.location.assign(url);
  };

  const save = async () => {
    try {
      setError('');
      await MoloniService.selectCompany(storeId, Number(companyId), Number(documentSetId));
      setEditing(false);
      load();
    } catch (requestError) {
      setError(describeInvoiceError(requestError, t));
    }
  };

  const startEdit = async () => {
    setError('');
    setEditing(true);
    try {
      const data = await MoloniService.getConnection(storeId, true);
      const companies = data.companies || data.Companies || [];
      setConnection(data);
      setCompanyId(data.companyId ?? data.CompanyId ?? (companies[0] ? (companies[0].companyId || companies[0].CompanyId) : ''));
      setDocumentSetId(data.documentSetId || data.DocumentSetId || '');
      setSets(data.documentSets || data.DocumentSets || []);
    } catch (requestError) {
      setError(describeInvoiceError(requestError, t));
    }
  };

  const disconnect = async () => {
    try {
      setError('');
      await MoloniService.disconnect(storeId);
      setConfirmDisconnect(false);
      setEditing(false);
      setCompanyId('');
      setDocumentSetId('');
      setSets([]);
      load();
    } catch (requestError) {
      setError(describeInvoiceError(requestError, t));
    }
  };

  if (!connection) {
    return error ? <Typography color="error">{error}</Typography> : null;
  }

  const ready = isReady;
  const companies = connection.companies || connection.Companies || [];
  const showSelection = (!ready || editing) && (companies.length > 0 || sets.length > 0);

  if (ready && !editing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          p: 2,
          border: `1px solid ${BRAND.line}`,
          borderRadius: 2,
          backgroundColor: BRAND.surface,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('moloni.title')}</Typography>
          <Typography variant="body2" sx={{ color: BRAND.textMuted }}>
            {t('moloni.ready', { company: connection.companyName || connection.CompanyName })}
          </Typography>
        </Box>
        {canWrite ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Button color="inherit" onClick={startEdit}>{t('moloni.changeCompany')}</Button>
            <Button color="error" onClick={() => setConfirmDisconnect(true)}>{t('moloni.disconnect')}</Button>
          </Box>
        ) : null}
        {error ? <Typography color="error" variant="body2">{error}</Typography> : null}
        <ConfirmDialog
          open={confirmDisconnect}
          onClose={() => setConfirmDisconnect(false)}
          onConfirm={disconnect}
          title={t('moloni.disconnectTitle')}
          message={t('moloni.disconnectMessage')}
          confirmText={t('moloni.disconnect')}
          cancelText={t('common.cancel')}
          confirmColor="error"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%', textAlign: 'center', py: { xs: 1, sm: 3 } }}>
      {!editing ? (
        <Typography sx={{ maxWidth: 520, textAlign: 'center' }}>{t('moloni.explanation')}</Typography>
      ) : null}
      {!ready && canWrite ? (
        <PrimaryActionButton onClick={connect} sx={{ width: '100%', maxWidth: 420 }}>{t('moloni.connect')}</PrimaryActionButton>
      ) : null}
      {showSelection ? (
        <Box sx={{ width: '100%', maxWidth: 420, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {companies.length > 0 ? (
            <SalovareSelectField
              label={t('moloni.company')}
              value={companyId}
              onChange={(event) => {
                setCompanyId(event.target.value);
                setDocumentSetId('');
                setSets([]);
                setError('');
              }}
              displayEmpty
            >
              {companies.map((company) => (
                <MenuItem key={company.companyId || company.CompanyId} value={company.companyId || company.CompanyId}>
                  {company.name || company.Name}
                </MenuItem>
              ))}
            </SalovareSelectField>
          ) : null}
          {sets.length > 0 ? (
            <SalovareSelectField label={t('moloni.series')} value={documentSetId} onChange={(event) => setDocumentSetId(event.target.value)} displayEmpty>
              {sets.map((set) => (
                <MenuItem key={set.documentSetId || set.DocumentSetId} value={set.documentSetId || set.DocumentSetId}>
                  {set.name || set.Name}
                </MenuItem>
              ))}
            </SalovareSelectField>
          ) : null}
          {companies.length > 0 && canWrite ? (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {editing ? <Button color="inherit" onClick={() => setEditing(false)}>{t('common.cancel')}</Button> : null}
              <PrimaryActionButton onClick={save} disabled={!companyId || !documentSetId}>{t('moloni.save')}</PrimaryActionButton>
            </Box>
          ) : null}
        </Box>
      ) : null}
      {error ? <Typography color="error">{error}</Typography> : null}
    </Box>
  );
};

export default IssueInvoiceDialog;

export const CopyInvoiceLinkButton = ({ storeId, sourceKey }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const copy = async () => {
    try {
      setError('');
      setMessage('');
      const url = await MoloniService.link(storeId, sourceKey);
      await navigator.clipboard.writeText(url);
      setMessage(t('moloni.linkCopied'));
    } catch (requestError) {
      setMessage('');
      setError(describeInvoiceError(requestError, t));
    }
  };

  return (
    <Box>
      <Button
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          copy();
        }}
        startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
        aria-label={t('moloni.copyLink')}
        sx={{ px: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
      >
        {t('moloni.receipt')}
      </Button>
      {message ? <Typography variant="caption" sx={{ display: 'block' }}>{message}</Typography> : null}
      {error ? <Typography variant="caption" color="error" sx={{ display: 'block' }}>{error}</Typography> : null}
    </Box>
  );
};

export const AppointmentInvoiceBar = ({ storeId, appointment, canIssue }) => {
  const { t } = useTranslation();
  const [flag, setFlag] = useState(null);
  const [open, setOpen] = useState(false);
  const sourceKey = appointment?.id ? String(appointment.id).replace(/-/g, '') : '';
  const paid = getPaymentStatusKey(appointment?.paymentStatus) === 'paid'
    || getAppointmentStatusKey(appointment?.status) === 'completed';

  const refresh = useCallback(() => {
    if (!storeId || !sourceKey) return;
    MoloniService.flags(storeId, [sourceKey])
      .then((list) => setFlag((list || []).find((item) => (item.sourceKey || item.SourceKey) === sourceKey) || null))
      .catch(() => setFlag(null));
  }, [storeId, sourceKey]);

  useEffect(() => {
    if (!canIssue || !paid) return undefined;
    refresh();
    return undefined;
  }, [canIssue, paid, refresh]);

  if (!paid || !canIssue) return null;

  const issued = Boolean(flag?.issued ?? flag?.Issued);

  return (
    <Box sx={{ mt: issued ? 1.5 : 1 }}>
      {canIssue && !issued ? (
        <PrimaryActionButton onClick={() => setOpen(true)} sx={{ mt: 1 }}>{t('moloni.issue')}</PrimaryActionButton>
      ) : null}
      {issued ? <CopyInvoiceLinkButton storeId={storeId} sourceKey={sourceKey} /> : null}
      <IssueInvoiceDialog
        open={open}
        storeId={storeId}
        sourceKey={sourceKey}
        onClose={() => setOpen(false)}
        onIssued={(result) => {
          setOpen(false);
          setFlag({
            sourceKey,
            issued: true,
            documentNumber: result?.documentNumber || result?.DocumentNumber || '',
          });
          refresh();
        }}
      />
    </Box>
  );
};
